import React, { useState } from 'react';
import { PlayIcon } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { CatalogRow } from './CatalogRow';
import { DemoBadge } from './DemoBadge';
import { Logo } from './Logo';
import { CATEGORY_LABELS, formatDuration, formatReleaseDate, getMetaParts, hasPlayableSource, isUpcoming } from '../utils/production';
import { buttonClass, pagePadding } from '../utils/styles';
import type { Production } from '../types/production';
interface ProductionViewProps {
  production: Production;
  related?: Production[];
  autoPlay?: boolean;
  notice?: React.ReactNode;
  showRelated?: boolean;
}
export function ProductionView({
  production,
  related = [],
  autoPlay = false,
  notice,
  showRelated = true
}: ProductionViewProps) {
  const [playRequest, setPlayRequest] = useState(0);
  const upcoming = isUpcoming(production);
  const releaseLabel = formatReleaseDate(production.releaseDate);
  const playable = hasPlayableSource(production.videoUrl) && !upcoming;
  const meta = getMetaParts(production);
  const details = [{
    label: 'Categoría',
    value: production.category ? CATEGORY_LABELS[production.category] : null
  }, {
    label: 'Año',
    value: production.year ? String(production.year) : null
  }, {
    label: 'Duración',
    value: formatDuration(production.durationMinutes)
  }, {
    label: 'Colección',
    value: production.collection
  }, {
    label: 'Estreno',
    value: upcoming ? releaseLabel : null
  }].filter((d): d is {
    label: string;
    value: string;
  } => !!d.value);
  return <article className="relative" data-id="element-235" data-mp="1hkt4ttc1og6y">
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[75vh] overflow-hidden" data-id="element-236" data-mp="1mjno0wdxc6rl">
        {production.posterUrl && <img src={production.posterUrl} alt="" className="h-full w-full object-cover opacity-30" data-id="element-237" data-mp="1qmagmzif5vpu" />}
        <div className="absolute inset-0 bg-gradient-to-b from-night/50 via-night/85 to-night" data-id="element-238" data-mp="2xy13pdyixfwj" />
      </div>

      <div className={`relative pb-14 pt-24 md:pt-28 ${pagePadding}`} data-id="element-239" data-mp="31ytdq9f7zotv">
        {notice}
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.75fr)_minmax(300px,1fr)] lg:gap-12 xl:gap-16" data-id="element-240" data-mp="12ubgfsy0at70">
          <VideoPlayer src={production.videoUrl} poster={production.posterUrl} title={production.title} autoPlay={autoPlay} playRequest={playRequest} availableFromLabel={upcoming ? releaseLabel ?? 'próximamente' : null} data-id="element-241" data-mp="xw1lnjg71ulx" />

          <div className="flex flex-col" data-id="element-242" data-mp="j86p1rz2f63k">
            <div className="flex items-center gap-2.5" data-id="element-243" data-mp="f6brmyk99cv2">
              {production.isOriginal && <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-mist" data-id="element-244" data-mp="sx6thqwc6tp5">
                  <Logo variant="mark" className="h-5 w-5 text-[11px]" data-id="element-245" data-mp="o6hyw2pd1gls" />
                  NICOLE Original
                </span>}
              {production.isDemo && <DemoBadge data-id="element-246" data-mp="3rngo0r7nrv74" />}
            </div>

            <h1 className="mt-4 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white md:text-5xl" data-id="element-247" data-mp="3rmp4wfv8zpxq">
              {production.title}
            </h1>

            {meta.length > 0 && <p className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-white/90" data-id="element-248" data-mp="2bxpn48vspogk">
                {meta.map((part, i) => <React.Fragment key={part} data-id="element-249" data-mp="233b7u6b9mdxg" data-mpi={i}>
                    {i > 0 && <span aria-hidden="true" className="h-1 w-1 rounded-full bg-mist" data-id="element-250" data-mp="231qdohsv4x04" />}
                    <span data-id="element-251" data-mp="2qu6t0kl7uavq">{part}</span>
                  </React.Fragment>)}
              </p>}

            {production.synopsis && <p className="mt-5 text-base leading-relaxed text-mist" data-id="element-252" data-mp="2gw0xckq060zb">{production.synopsis}</p>}

            <div className="mt-7" data-id="element-253" data-mp="2lt9b8944biur">
              <button type="button" disabled={!playable} onClick={() => setPlayRequest(n => n + 1)} className={buttonClass('primary', 'lg', 'w-full sm:w-auto')} data-id="element-254" data-mp="2lwf29twb4ut4">
                <PlayIcon className="h-5 w-5 fill-current" data-id="element-255" data-mp="1pp29quz75gpu" />
                {upcoming ? 'Próximamente' : playable ? 'Reproducir' : 'Video no disponible'}
              </button>
            </div>

            {details.length > 0 && <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-graphite pt-6" data-id="element-256" data-mp="1gx0a2ywyc4gm">
                {details.map(d => <div key={d.label} data-id="element-257" data-mp="28no9a5f2ezrn">
                    <dt className="text-xs uppercase tracking-[0.14em] text-mist" data-id="element-258" data-mp="28mn0k7liupd3">{d.label}</dt>
                    <dd className="mt-1 text-sm font-medium text-white" data-id="element-259" data-mp="24n4ev9lvma97">{d.value}</dd>
                  </div>)}
              </dl>}
          </div>
        </div>
      </div>

      {showRelated && <div className="pb-20" data-id="element-260" data-mp="1undmpeksg7ca">
          <CatalogRow id="mas-creaciones" title="Más creaciones de Nicole" productions={related} emptyMessage="Las próximas creaciones de Nicole aparecerán aquí cuando se publiquen." data-id="element-261" data-mp="o7tdw6kyzqyj" />
        </div>}
    </article>;
}